"use client";

import {
  Form,
  FormCheckbox,
  FormInput,
  FormStatus,
  FormTextarea,
  useFormStatus,
} from "@/components/primitives/Form";
import { Button } from "@/components/primitives/Button";
import { ConsentPrivacyLabel } from "@/components/forms/ConsentPrivacyLabel";
import { getFormsCopy } from "@/content/forms";
import { requestInvestorDeck } from "@/lib/api/endpoints";
import { buildFormConsentRecord } from "@/lib/consent";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { investorDeckInputSchema } from "@/lib/api/types";
import { withAttribution } from "@/lib/utm";

export function InvestorDeckForm({ className = "" }: { className?: string }) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  return (
    <Form
      schema={investorDeckInputSchema}
      trackingName="investor_deck"
      successMessage={forms.investorDeck.successMessage}
      className={className}
      onSubmit={async (values, signal) => {
        const { consentGiven, ...input } = values;
        if (!consentGiven) return;

        const consentRecord = buildFormConsentRecord({
          consentText: forms.investorDeck.consentTextRecord,
          locale,
          formSource: "investor_deck",
        });

        await requestInvestorDeck(
          withAttribution(
            { ...input, locale },
            { entryPoint: "investor_deck", sourceBase: "investor_deck" },
          ),
          consentRecord,
          signal,
        );
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormInput name="name" label={forms.investorDeck.nameLabel} required autoComplete="name" />
          <FormInput
            name="email"
            type="email"
            label={forms.investorDeck.workEmailLabel}
            required
            autoComplete="email"
          />
        </div>
        <FormInput
          name="fund"
          label={forms.investorDeck.fundLabel}
          autoComplete="organization"
        />
        <FormTextarea
          name="message"
          label={forms.investorDeck.messageLabel}
          rows={3}
          placeholder={forms.investorDeck.messagePlaceholder}
        />
        <FormCheckbox
          name="consentGiven"
          required
          label={<ConsentPrivacyLabel text={forms.investorDeck.consentLabel} />}
        />
        <Submit locale={locale} />
        <FormStatus />
      </div>
    </Form>
  );
}

function Submit({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);
  const status = useFormStatus();
  const busy = status === "submitting";
  return (
    <Button type="submit" variant="primary" size="lg" disabled={busy}>
      {busy ? forms.investorDeck.busyLabel : forms.investorDeck.submitLabel}
    </Button>
  );
}
